/**
 * 信息图表演示 - 数据对比效果
 * 
 * 基于足球视频质量标准（9.0/10）制作
 * 
 * 核心功能：
 * 1. 数据对比标题（动画）
 * 2. 对比条形图（两组数据）
 * 3. 差异高亮（百分比变化）
 * 4. 数据摘要（关键指标）
 * 
 * 技术亮点：
 * - Spring 物理动画
 * - interpolate 插值计算
 * - 数据驱动可视化
 * - 模块化设计
 * - 详细注释
 * 
 * 适用场景：
 * - 商业报告视频
 * - 数据分析展示
 * - A/B 测试对比
 * - 市场调研报告
 * - 绩效对比视频
 * 
 * @author 龙虾菌 🦞
 * @date 2026-03-10
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

// ==================== 类型定义 ====================

/**
 * 对比数据项
 */
interface ComparisonItem {
  label: string;
  valueA: number;
  valueB: number;
  unit?: string;
}

/**
 * 对比数据配置
 */
interface ComparisonData {
  title: string;
  subtitle: string;
  datasetA: {
    name: string;
    color: string;
  };
  datasetB: {
    name: string;
    color: string;
  };
  items: ComparisonItem[];
}

// ==================== 配置数据 ====================

/**
 * 对比数据配置（可自定义修改）
 */
const COMPARISON_DATA: ComparisonData = {
  title: '2025 Q4 季度对比报告',
  subtitle: '业绩增长显著，各项指标均超预期',
  datasetA: {
    name: '2024 Q4',
    color: '#3b82f6',
  },
  datasetB: {
    name: '2025 Q4',
    color: '#10b981',
  },
  items: [
    { label: '营收', valueA: 850, valueB: 1200, unit: '万元' },
    { label: '用户数', valueA: 45000, valueB: 78000, unit: '' },
    { label: '订单量', valueA: 8900, valueB: 15600, unit: '' },
    { label: '转化率', valueA: 3.2, valueB: 4.8, unit: '%' },
  ],
};

// ==================== 子组件 ====================

/**
 * 标题组件
 */
const ComparisonTitle: React.FC<{
  title: string;
  subtitle: string;
  frame: number;
  fps: number;
}> = ({ title, subtitle, frame, fps }) => {
  const titleSpring = spring({
    frame,
    fps,
    config: { damping: 15, stiffness: 100, mass: 0.5 },
  });

  return (
    <div style={{ 
      position: 'absolute', 
      top: 60, 
      left: 80, 
      right: 80,
      height: 120, // 固定高度，防止重叠
    }}>
      <h1
        style={{
          fontSize: 56, // 减小字号：64 → 56
          fontWeight: 'bold',
          color: '#fff',
          margin: 0,
          textShadow: '0 0 40px rgba(59, 130, 246, 0.6)',
          opacity: titleSpring,
        }}
      >
        {title}
      </h1>
      <p
        style={{
          fontSize: 24, // 减小字号：28 → 24
          color: 'rgba(255, 255, 255, 0.8)',
          margin: '12px 0 0 0', // 减小 margin：15px → 12px
          opacity: titleSpring,
        }}
      >
        {subtitle}
      </p>
    </div>
  );
};

/**
 * 图例组件
 */
const Legend: React.FC<{
  datasetA: { name: string; color: string };
  datasetB: { name: string; color: string };
  frame: number;
  fps: number;
}> = ({ datasetA, datasetB, frame, fps }) => {
  const legendSpring = spring({
    frame: frame - 20,
    fps,
    config: { damping: 15, stiffness: 80 },
  });

  return (
    <div
      style={{
        position: 'absolute',
        top: 200, // 调整：180 → 200，避免与副标题重叠
        left: 80,
        display: 'flex',
        gap: 40,
        transform: `translateY(${interpolate(legendSpring, [0, 1], [20, 0])}px)`,
        opacity: legendSpring,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          style={{
            width: 24,
            height: 24,
            background: datasetA.color,
            borderRadius: 6,
          }}
        />
        <div style={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.9)' }}>
          {datasetA.name}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          style={{
            width: 24,
            height: 24,
            background: datasetB.color,
            borderRadius: 6,
          }}
        />
        <div style={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.9)' }}>
          {datasetB.name}
        </div>
      </div>
    </div>
  );
};

/**
 * 对比条形图组件
 */
const ComparisonBar: React.FC<{
  item: ComparisonItem;
  index: number;
  datasetA: { name: string; color: string };
  datasetB: { name: string; color: string };
  frame: number;
  fps: number;
}> = ({ item, index, datasetA, datasetB, frame, fps }) => {
  const delay = 40 + index * 20;
  const maxValue = Math.max(item.valueA, item.valueB);

  // 条形图 Spring 动画
  const barSpring = spring({
    frame: frame - delay,
    fps,
    config: { damping: 15, stiffness: 80, mass: 0.6 },
  });

  // 数据 A 的宽度（减少到 280）
  const widthA = (item.valueA / maxValue) * 280 * interpolate(barSpring, [0, 1], [0, 1]);
  // 数据 B 的宽度（减少到 280）
  const widthB = (item.valueB / maxValue) * 280 * interpolate(barSpring, [0, 1], [0, 1]);

  // 增长百分比
  const growth = ((item.valueB - item.valueA) / item.valueA) * 100;
  const growthText = growth > 0 ? `+${growth.toFixed(1)}%` : `${growth.toFixed(1)}%`;
  const growthColor = growth > 0 ? '#10b981' : '#ef4444';

  // 数字滚动效果
  const displayValueA = Math.round(item.valueA * interpolate(frame, [delay + 20, delay + 50], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  }));
  const displayValueB = Math.round(item.valueB * interpolate(frame, [delay + 20, delay + 50], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  }));

  return (
    <div
      style={{
        marginBottom: 50,
        transform: `translateY(${interpolate(barSpring, [0, 1], [40, 0])}px)`,
        opacity: barSpring,
      }}
    >
      {/* 标签 */}
      <div
        style={{
          fontSize: 24,
          fontWeight: 'bold',
          color: '#fff',
          marginBottom: 15,
        }}
      >
        {item.label}
      </div>

      {/* 对比条形图 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
        {/* 数据 A */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
          <div
            style={{
              width: widthA,
              height: 40,
              background: datasetA.color,
              borderRadius: 8,
              boxShadow: `0 4px 12px ${datasetA.color}66`,
            }}
          />
          <div style={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.9)', width: 80 }}>
            {displayValueA.toLocaleString()}
            {item.unit}
          </div>
        </div>

        {/* VS 分隔符 */}
        <div
          style={{
            width: 40,
            height: 40,
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
            fontWeight: 'bold',
            color: 'rgba(255, 255, 255, 0.7)',
          }}
        >
          VS
        </div>

        {/* 数据 B */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
          <div
            style={{
              width: widthB,
              height: 40,
              background: datasetB.color,
              borderRadius: 8,
              boxShadow: `0 4px 12px ${datasetB.color}66`,
            }}
          />
          <div style={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.9)', width: 80 }}>
            {displayValueB.toLocaleString()}
            {item.unit}
          </div>
        </div>

        {/* 增长百分比 */}
        <div
          style={{
            marginLeft: 20,
            padding: '8px 16px',
            background: `${growthColor}22`,
            borderRadius: 12,
            border: `2px solid ${growthColor}`,
            fontSize: 20,
            fontWeight: 'bold',
            color: growthColor,
          }}
        >
          {growthText}
        </div>
      </div>
    </div>
  );
};

/**
 * 数据摘要组件
 */
const SummaryCard: React.FC<{
  frame: number;
  fps: number;
  data: ComparisonData;
}> = ({ frame, fps, data }) => {
  const delay = 120;

  const summarySpring = spring({
    frame: frame - delay,
    fps,
    config: { damping: 15, stiffness: 80 },
  });

  // 计算平均增长率
  const avgGrowth =
    data.items.reduce((sum, item) => {
      return sum + ((item.valueB - item.valueA) / item.valueA) * 100;
    }, 0) / data.items.length;

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 60,
        left: 80,
        right: 80,
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 20,
        transform: `translateY(${interpolate(summarySpring, [0, 1], [30, 0])}px)`,
        opacity: summarySpring,
      }}
    >
      {data.items.map((item, index) => {
        const growth = ((item.valueB - item.valueA) / item.valueA) * 100;
        const growthColor = growth > 0 ? '#10b981' : '#ef4444';

        return (
          <div
            key={index}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              padding: 20,
              borderRadius: 16,
              border: '2px solid rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <div style={{ fontSize: 26, color: 'rgba(255, 255, 255, 0.7)', marginBottom: 8 }}>
              {item.label}
            </div>
            <div
              style={{
                fontSize: 32,
                fontWeight: 'bold',
                color: growthColor,
              }}
            >
              {growth > 0 ? '+' : ''}
              {growth.toFixed(1)}%
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ==================== 主组件 ====================

/**
 * 信息图表演示 - 数据对比效果
 * 
 * 参数：
 * - data: ComparisonData - 对比数据（可选，默认使用 COMPARISON_DATA）
 */
export const DataComparison: React.FC<{ data?: ComparisonData }> = ({
  data = COMPARISON_DATA,
}) => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #1e3a8a 0%, #312e81 50%, #4c1d95 100%)',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      {/* 标题 */}
      <ComparisonTitle
        title={data.title}
        subtitle={data.subtitle}
        frame={frame}
        fps={fps}
      />

      {/* 图例 */}
      <Legend
        datasetA={data.datasetA}
        datasetB={data.datasetB}
        frame={frame}
        fps={fps}
      />

      {/* 对比条形图 */}
      <div
        style={{
          position: 'absolute',
          top: 260,
          left: 80,
          right: 80,
        }}
      >
        {data.items.map((item, index) => (
          <ComparisonBar
            key={index}
            item={item}
            index={index}
            datasetA={data.datasetA}
            datasetB={data.datasetB}
            frame={frame}
            fps={fps}
          />
        ))}
      </div>

      {/* 数据摘要 */}
      <SummaryCard frame={frame} fps={fps} data={data} />

      {/* 装饰元素（渐变光晕） */}
      <div
        style={{
          position: 'absolute',
          top: -200,
          right: -200,
          width: 600,
          height: 600,
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.2) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(80px)',
        }}
      />
    </AbsoluteFill>
  );
};

// ==================== 默认配置 ====================

/**
 * 默认导出配置
 */
export default {
  id: 'DataComparison',
  component: DataComparison,
  durationInFrames: 150, // 5 秒 @ 30 FPS
  fps: 30,
  width: 1920,
  height: 1080,
};
